import React, { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getPreviousSelection,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  $getNodeByKey,
} from 'lexical';

import {
  ImageNode,
  INSERT_IMAGE_COMMAND,
  ImagePayload,
  $createImageNode,
  $isImageNode,
  INSERT_LOADING_IMAGE_COMMAND,
} from '../nodes/ImageNode';
import loading from 'src/assets/loading.gif';
import {
  $createAttachedNode,
  $isAttachedNode,
  AttachedNode,
  AttachedNodePayload,
  INSERT_ATTACHED_NODE_COMMAND,
  INSERT_LOADING_ATTACHED_NODE_COMMAND,
} from '../nodes/AttachedNode';

const UploadPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<Partial<ImagePayload>>(
        INSERT_LOADING_IMAGE_COMMAND,
        (payload) => {
          const selection = $getSelection();

          if (!selection) {
            const previousSel = $getPreviousSelection();
            if ($isRangeSelection(previousSel)) {
              previousSel.insertNodes([
                $createImageNode({
                  src: loading,
                  altText: '',
                  name: payload.name,
                }),
              ]);
            }
          }

          if ($isRangeSelection(selection)) {
            selection.insertNodes([
              $createImageNode({
                src: loading,
                altText: '',
                name: payload.name,
              }),
            ]);
          }

          return false;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<ImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const decoNode = editor.getDecorators() as any;

          for (const key in decoNode) {
            const node = $getNodeByKey(key);
            if ($isImageNode(node)) {
              if (node.getFileName() === payload.name) {
                node.getWritable().setSrc(payload.src);
                node.getWritable().setAltText(payload.altText);
              }
            }
          }

          return false;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  useEffect(() => {
    if (!editor.hasNodes([AttachedNode])) {
      throw new Error('AttachedPlugin: AttachedNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<AttachedNodePayload>(
        INSERT_LOADING_ATTACHED_NODE_COMMAND,
        (payload) => {
          const selection = $getSelection();

          if (!selection) {
            const previousSel = $getPreviousSelection();
            if ($isRangeSelection(previousSel)) {
              previousSel.insertNodes([
                $createAttachedNode({
                  fileName: payload.fileName,
                  fileType: 'loading',
                }),
              ]);
            }
          }

          if ($isRangeSelection(selection)) {
            selection.insertNodes([
              $createAttachedNode({
                fileName: payload.fileName,
                fileType: 'loading',
              }),
            ]);
          }

          return false;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<AttachedNodePayload>(
        INSERT_ATTACHED_NODE_COMMAND,
        (payload) => {
          const decoNode = editor.getDecorators() as any;

          for (const key in decoNode) {
            const node = $getNodeByKey(key);
            if ($isAttachedNode(node)) {
              if (node.getFileName() === payload.fileName) {
                node.getWritable().setUrl(payload.url);
                node.getWritable().setFileName(payload.fileName);
                node.getWritable().setFileType(payload.fileType);
              }
            }
          }

          return false;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  return null;
};

export default UploadPlugin;
export { ImageNode, INSERT_IMAGE_COMMAND, $createImageNode, $isImageNode };
